import Tone from 'tone';
import {PATTERN_SWITCH} from 'session/constants.js';
import {setMixEvents} from 'actions/index.js';
import Resolution from 'engine/Resolution.js';
import PatternSwitchEvent from 'session/PatternSwitchEvent.js';
import TrackVolumeChangeEvent from 'session/TrackVolumeChangeEvent.js';
import {TRACK_VOLUME_CHANGE} from 'session/constants.js';
import GroupVolumeChangeEvent from 'session/GroupVolumeChangeEvent.js';
import {GROUP_VOLUME_CHANGE} from 'session/constants.js';
import TrackMuteEvent from 'session/TrackMuteEvent.js';
import {TRACK_MUTE} from 'session/constants.js';
import GroupMuteEvent from 'session/GroupMuteEvent.js';
import {GROUP_MUTE} from 'session/constants.js';
import ParameterChangeEvent from 'session/ParameterChangeEvent.js';
import {PARAMETER_CHANGE} from 'session/constants.js';
import TrackSendVolumeChangeEvent from 'session/TrackSendVolumeChangeEvent.js';
import {TRACK_SEND_VOLUME_CHANGE} from 'session/constants.js';
import TrackSoloEvent from 'session/TrackSoloEvent.js';
import {TRACK_SOLO} from 'session/constants.js';
import TrackReverseEvent from 'session/TrackReverseEvent.js';
import {TRACK_REVERSE,
        SEQUENCE_ROLL,
        BASIC,
        RESOLUTION_CHANGE,
       } from 'session/constants.js';
import SequenceRollEvent from 'session/SequenceRollEvent.js';
import ResolutionChangeEvent from 'session/ResolutionChangeEvent.js';
import {loadEventFromJson} from './loadEventFromJson.js';

class MixByType {
    constructor(type, subSequence) {
        this.type = type;

        this.events = [];

        // keeps track of how many 1/32th steps have elapsed in this performance
        this.counter = 0;

        this.subSequence = subSequence;
    }

    getMixType() {
        return BASIC;
    }

    seekToTrackStep(trackStep) {
        if (this.events.length > 0 && this.events[this.events.length - 1].step < trackStep) {
            // dont restart
        } else {
            this.restart();
        }
    }

    restart() {
        this.counter = 0;
        this.events.forEach(e => e.restart());
    }

    async scheduleNext(step, timeUntilEvent, sequencerEngine) {
        if (step == 0) {
            this.restart();
        }

        let i = this.counter;

        let events = this.events;
        // sees if any events are up for this track step
        for (; i < events.length; i++) {
            if (step === events[i].step) {
                // schedule the event because we are at the right step, or we missed it
                // and probably should do it again
                events[i].scheduleEvent(timeUntilEvent, sequencerEngine, events[i+1]);
            } else if (step > events[i].step &&
                       !events[i].isSkippable()){
                // we missed this event, but since it's an important event (i.e.
                // not skippable), we still run it
                events[i].scheduleEvent(0 - events[i].time, sequencerEngine, events[i+1]);
            } else if (step < events[i].step) {
                break;
            } else {
            }
        }

        this.counter = i;
    }

    eraseEventsAtStep(step) {
        this.events = this.events.filter(
            event => event.step != step);
    }

    schedule() {
        this.events.forEach(
            event => event.scheduleEvent());
    }

    getJson() {
        return {
            type: this.type,
            events: this.events.map(
                event => event.getJson())
                .filter(event => event !== undefined)
        };
    }

    getCopy() {
        let mix = new MixByType(this.type);
        mix.events = this.events;
        return mix;
    }

    loadFromJson(json, sequencerEngine, subSequence) {
        for (let i in json.events) {
            let eventJson = json.events[i];
            let event = loadEventFromJson(
                eventJson, this.type, sequencerEngine, subSequence);
            if (event !== undefined) {
                if (this.events.length > 0 && event.step < this.events[this.events.length - 1].step) {
                    // find the last do over and use that
                    if (json.length - i >= 500) {
                        this.events = [];
                    }
                }
                this.events.push(event);
            }
        }
    }

    calculateDurations() {
        let previous = undefined;
        let events = [];
        for (let i in this.events) {
            let event = this.events[i];
            if (event === undefined || event.isEmpty()) {
                continue;
            }

            if (previous !== undefined &&
                !previous.rolling && !event.rolling) {
                continue;
            }
            events.push(event);

            if (previous !== undefined) {
                previous.duration = this.events[i].step - previous.step;
            }

            previous = this.events[i];
        }

        this.events = events;
    }

    clear() {
        this.events = [];
        this.counter = 0;
    }

    getNextEvent(event, useCounter=false) {
        for (let i=useCounter ? this.counter : 0; i < this.events.length; i++) {
            if (event.step < this.events[i].step) {
                return this.events[i];
            }
        }
        return undefined;
    }

    getPreviousEvent(event, useEquals=true, useCounter=false) {
        let previousEvent = undefined;
        for (let i=useCounter ? Math.max(0, this.counter - 1) : 0; i < this.events.length; i++) {
            if (useEquals && event.step <= this.events[i].step) {
                return previousEvent;
            } else if (!useEquals && event.step < this.events[i].step) {
                return previousEvent;
            }
            previousEvent = this.events[i];
        }
        return previousEvent;
    }

    updateRedux(subSequence, sequencerEngine) {
        sequencerEngine.store.dispatch(setMixEvents(subSequence.id, this.type, [... this.events]));
    }

    prune() {
        if (this.events.every(e => e.isPrunable())) {
            this.events = [];
        }

        let previous = undefined;
        let events = [];
        for (let i in this.events) {
            let event = this.events[i];
            if (previous !== undefined &&
                event.isPrunable() && previous.isPrunable()) {
                continue;
            }
            if (previous === undefined && event.isPrunable()) {
                continue;
            }
            events.push(event);
            previous = event;
        }

        this.events = events;
    }

    getEventsBetween(start, end, inclusive=false) {
        if (inclusive) {
            return this.events.filter(
                e => e.step >= start && e.step <= end);
        } else {
            return this.events.filter(
                e => e.step >= start && e.step < end);
        }
    }
}

export default MixByType;
